import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../../utils/authUtils';
import '../../css/adminDashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const formatVNDate = (d) => {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleString('vi-VN', { hour12: false });
  } catch {
    return d;
  }
};

export default function AdminBlogs() {
  const navigate = useNavigate();
  const [view, setView] = useState('blogs'); // 'blogs' | 'categories'
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');

  const [modal, setModal] = useState(null); // { mode: 'create'|'edit', data? }
  const [form, setForm] = useState({ title: '', summary: '', content: '', image: '', categoryId: '', status: 1 });
  const [categories, setCategories] = useState([]);

  // Category management state
  const [categoryRows, setCategoryRows] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [categoryModal, setCategoryModal] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', status: 1 });

  const normalize = (r) => ({
    blogID: r.BlogID ?? r.blogID ?? r.id,
    title: r.Title ?? r.title,
    summary: r.Summary ?? r.summary,
    content: r.Content ?? r.content,
    image: r.Image ?? r.image,
    categoryID: r.CategoryID ?? r.categoryID ?? null,
    categoryName: r.CategoryName ?? r.categoryName ?? r.Category ?? r.category ?? '',
    authorID: r.AuthorID ?? r.authorID,
    createdAt: r.CreatedAt ?? r.createdAt,
    status: typeof r.Status === 'number' ? r.Status : (r.Status ? 1 : 0),
  });

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blogs`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Không thể tải danh sách');
      let list = Array.isArray(json.data) ? json.data.map(normalize) : [];
      if (q) list = list.filter(x => (x.title || '').toLowerCase().includes(q.toLowerCase()) || (x.summary || '').toLowerCase().includes(q.toLowerCase()));
      if (status !== '') list = list.filter(x => String(x.status) === String(status));
      if (category) list = list.filter(x => (x.categoryName || '').toLowerCase().includes(category.toLowerCase()));
      setRows(list);
    } catch (e) {
      setError(e?.message || 'Lỗi tải dữ liệu');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blog-categories`);
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success) {
        setCategories(json.data || []);
      }
    } catch (e) {

    }
  };

  // Category CRUD functions
  const loadCategoryRows = async () => {
    try {
      setCategoryLoading(true);
      setCategoryError('');
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blog-categories`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Lỗi tải danh sách');
      setCategoryRows(json.data || []);
    } catch (e) {
      setCategoryError(e?.message || 'Lỗi khi tải dữ liệu');
      setCategoryRows([]);
    } finally {
      setCategoryLoading(false);
    }
  };

  const openCategoryCreate = () => {
    setCategoryForm({ name: '', description: '', status: 1 });
    setCategoryModal({ mode: 'create' });
  };

  const openCategoryEdit = (row) => {
    setCategoryForm({ name: row.Name, description: row.Description || '', status: row.Status });
    setCategoryModal({ mode: 'edit', id: row.CategoryID });
  };

  const submitCategoryCreate = async () => {
    if (!categoryForm.name.trim()) return alert('Vui lòng nhập tên chủ đề');
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blog-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Lỗi khi tạo');
      alert('Đã tạo chủ đề thành công');
      setCategoryModal(null);
      loadCategoryRows();
      loadCategories();
    } catch (e) {
      alert(e?.message || 'Lỗi khi tạo chủ đề');
    }
  };

  const submitCategoryEdit = async () => {
    if (!categoryForm.name.trim()) return alert('Vui lòng nhập tên chủ đề');
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blog-categories/${categoryModal.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm)
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Lỗi khi cập nhật');
      alert('Đã cập nhật chủ đề thành công');
      setCategoryModal(null);
      loadCategoryRows();
      loadCategories();
    } catch (e) {
      alert(e?.message || 'Lỗi khi cập nhật chủ đề');
    }
  };

  const deleteCategory = async (row) => {
    if (!window.confirm(`Xóa chủ đề "${row.Name}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blog-categories/${row.CategoryID}`, {
        method: 'DELETE'
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Lỗi khi xóa');
      alert('Đã xóa chủ đề');
      loadCategoryRows();
      loadCategories();
    } catch (e) {
      alert(e?.message || 'Lỗi khi xóa chủ đề');
    }
  };

  const toggleCategoryStatus = async (row) => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blog-categories/${row.CategoryID}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: row.Name, description: row.Description || '', status: row.Status ? 0 : 1 })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Lỗi cập nhật trạng thái');
      loadCategoryRows();
      loadCategories();
    } catch (e) {
      alert(e?.message || 'Lỗi cập nhật trạng thái');
    }
  };

  useEffect(() => { load(); loadCategories(); }, []);
  useEffect(() => { if (view === 'categories') loadCategoryRows(); }, [view]);

  const openCreate = () => {
    setForm({ title: '', summary: '', content: '', image: '', categoryId: '', status: 1 });
    setModal({ mode: 'create' });
  };

  const openEdit = (b) => {
    setForm({ title: b.title || '', summary: b.summary || '', content: b.content || '', image: b.image || '', categoryId: b.categoryID || '', status: b.status ?? 1 });
    setModal({ mode: 'edit', data: b });
  };

  const submitCreate = async () => {
    try {
      if (!form.title) { alert('Nhập tiêu đề'); return; }
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blogs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Tạo bài viết thất bại');
      setModal(null);
      load();
    } catch (e) { alert(e?.message || 'Lỗi tạo'); }
  };

  const submitEdit = async () => {
    try {
      const id = modal?.data?.blogID;
      if (!id) return;
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blogs/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Cập nhật thất bại');
      setModal(null);
      load();
    } catch (e) { alert(e?.message || 'Lỗi cập nhật'); }
  };

  const removeBlog = async (b) => {
    if (!window.confirm(`Xóa bài viết "${b.title}"?`)) return;
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blogs/${b.blogID}`, { method: 'DELETE' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Xóa thất bại');
      load();
    } catch (e) { alert(e?.message || 'Lỗi xóa'); }
  };

  const toggleBlogStatus = async (b) => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/admin/blogs/${b.blogID}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: b.status ? 0 : 1 })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Cập nhật trạng thái thất bại');
      load();
    } catch (e) { alert(e?.message || 'Lỗi cập nhật trạng thái'); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetchWithAuth(`${API_BASE}/api/upload/blog`, { method: 'POST', body: fd });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.imageUrl) throw new Error(json?.message || json?.error || 'Upload ảnh thất bại');
      setForm((f) => ({ ...f, image: json.imageUrl }));
    } catch (e1) { alert(e1?.message || 'Lỗi upload ảnh'); }
  };

  return (
    <div className="admin-page-container">
      <div className="admin-header">
        <h1><i className="fa-solid fa-newspaper"></i> Quản lý blog</h1>
        <small>Thêm, sửa, vô hiệu hóa bài viết. Người dùng chỉ có thể xem.</small>
      </div>

      <div style={{ marginBottom: 20 }}>
        <button
          className={`admin-btn ${view === 'blogs' ? 'admin-btn-view' : 'admin-btn-ghost'}`}
          onClick={() => setView('blogs')}
          style={{ marginRight: 8 }}
        >
          <i className="fa-solid fa-newspaper" style={{ marginRight: 6 }}></i>
          Bài viết
        </button>
        <button
          className={`admin-btn ${view === 'categories' ? 'admin-btn-view' : 'admin-btn-ghost'}`}
          onClick={() => setView('categories')}
        >
          <i className="fa-solid fa-tags" style={{ marginRight: 6 }}></i>
          Chủ đề
        </button>
      </div>

      {view === 'blogs' && (
      <div className="admin-table-card">
        <div className="admin-table-header">
          <div className="admin-search-box">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo tiêu đề/tóm tắt..." />
            <i className="fa-solid fa-search"></i>
          </div>
          <div className="admin-filters">
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Chủ đề"
              style={{ padding: '10px 15px', border: '1px solid #dfe4ea', borderRadius: 8, fontSize: 14 }}
            />
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Tất cả trạng thái</option>
              <option value="1">Hoạt động</option>
              <option value="0">Không hoạt động</option>
            </select>
            <button onClick={load} className="admin-btn admin-btn-view">
              <i className="fa-solid fa-filter" style={{ marginRight: 5 }}></i>
              Lọc
            </button>
            <button onClick={openCreate} className="admin-btn admin-btn-edit">
              <i className="fa-solid fa-plus" style={{ marginRight: 5 }}></i>
              Thêm bài viết
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tiêu đề</th>
                <th>Chủ đề</th>
                <th>Trạng thái</th>
                <th>Tạo lúc</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="admin-loading"><i className="fa-solid fa-spinner fa-spin"></i> Đang tải...</td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="admin-error"><i className="fa-solid fa-circle-exclamation"></i> {error}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#747d8c' }}>Không có dữ liệu</td></tr>
              ) : (
                rows.map((b) => (
                  <tr key={b.blogID}>
                    <td>#{b.blogID}</td>
                    <td style={{ fontWeight: 500 }}>{b.title}</td>
                    <td>{b.categoryName || <span style={{ color: '#a4b0be' }}>—</span>}</td>
                    <td>
                      {b.status ? (
                        <span className="admin-badge admin-badge-success">Hoạt động</span>
                      ) : (
                        <span className="admin-badge admin-badge-secondary">Không hoạt động</span>
                      )}
                    </td>
                    <td>{formatVNDate(b.createdAt)}</td>
                    <td>
                      <div className="admin-action-links">
                        <button className="admin-btn" onClick={() => toggleBlogStatus(b)}>
                          <i className="fa-solid fa-power-off"></i> Trạng thái
                        </button>
                        <button className="admin-btn admin-btn-edit" onClick={() => openEdit(b)}>
                          <i className="fa-solid fa-pen-to-square"></i> Sửa
                        </button>
                        <button className="admin-btn admin-btn-delete" onClick={() => removeBlog(b)}>
                          <i className="fa-solid fa-trash-can"></i> Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {modal && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800 }}>
            <div className="admin-modal-header">
              <h3>{modal.mode === 'create' ? 'Thêm bài viết mới' : 'Chỉnh sửa bài viết'}</h3>
              <button type="button" className="admin-modal-close" onClick={() => setModal(null)}>×</button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-info-grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 20 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <label>
                    <span>Tiêu đề <span style={{ color: '#ff4757' }}>*</span></span>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Nhập tiêu đề bài viết..."
                    />
                  </label>

                  <label>
                    <span>Tóm tắt</span>
                    <textarea
                      rows={4}
                      value={form.summary}
                      onChange={(e) => setForm({ ...form, summary: e.target.value })}
                      placeholder="Tóm tắt ngắn gọn về bài viết..."
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #dfe4ea', borderRadius: 6, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </label>

                  <label>
                    <span>Nội dung</span>
                    <textarea
                      rows={12}
                      value={form.content}
                      onChange={(e) => setForm({ ...form, content: e.target.value })}
                      placeholder="Nhập nội dung bài viết (có thể dùng HTML để định dạng)..."
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #dfe4ea', borderRadius: 6, fontSize: 14, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
                    />
                    <small style={{ color: '#747d8c', fontSize: 12, display: 'block', marginTop: 4 }}>Hỗ trợ HTML cơ bản: &lt;p&gt;, &lt;br&gt;, &lt;b&gt;, &lt;i&gt;, &lt;ul&gt;, &lt;li&gt;</small>
                  </label>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <label>
                    <span>Chủ đề</span>
                    <select
                      value={String(form.categoryId || '')}
                      onChange={(e) => setForm({ ...form, categoryId: e.target.value ? Number(e.target.value) : '' })}
                    >
                      <option value="">-- Chọn chủ đề --</option>
                      {categories.filter(c => c.Status === 1).map((c) => (
                        <option key={c.CategoryID} value={c.CategoryID}>{c.Name}</option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Trạng thái</span>
                    <select
                      value={String(form.status ?? 1)}
                      onChange={(e) => setForm({ ...form, status: Number(e.target.value) })}
                    >
                      <option value="1">Hoạt động</option>
                      <option value="0">Không hoạt động</option>
                    </select>
                  </label>

                  <label>
                    <span>Ảnh đại diện</span>
                    <input
                      type="text"
                      value={form.image}
                      onChange={(e) => setForm({ ...form, image: e.target.value })}
                      placeholder="/document/blog/blog-xxx.jpg"
                      style={{ marginBottom: 8 }}
                    />
                    <label
                      className="admin-btn admin-btn-view"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        cursor: 'pointer',
                        border: '2px dashed #dfe4ea',
                        borderRadius: 6,
                        background: '#f8f9fa'
                      }}
                    >
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                      <span>Upload ảnh</span>
                      <input type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
                    </label>
                    {form.image && (
                      <div style={{ marginTop: 8 }}>
                        <img
                          src={form.image.startsWith('http') ? form.image : `${API_BASE}${form.image}`}
                          alt="Preview"
                          style={{
                            width: '100%',
                            height: 140,
                            objectFit: 'cover',
                            borderRadius: 6,
                            border: '1px solid #dfe4ea'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                  </label>

                  <div className="admin-info-item" style={{ background: '#f1f2f6', border: '1px solid #dfe4ea' }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: '#2f3542', marginBottom: 8 }}>Gợi ý</label>
                    <div style={{ fontSize: 12, color: '#747d8c', lineHeight: 1.6 }}>
                      <div>• Kích thước ảnh: 800x400px</div>
                      <div>• Định dạng: JPG, PNG, GIF</div>
                      <div>• Bài viết hiển thị khi trạng thái là "Hoạt động"</div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 24,
                paddingTop: 20,
                borderTop: '1px solid #f1f2f6'
              }}>
                <button
                  className="admin-btn"
                  style={{ background: '#a4b0be', color: 'white' }}
                  onClick={() => setModal(null)}
                >
                  Hủy
                </button>
                {modal.mode === 'create' ? (
                  <button
                    className="admin-btn admin-btn-view"
                    onClick={submitCreate}
                  >
                    <i className="fa-solid fa-plus" style={{ marginRight: 5 }}></i>
                    Tạo bài viết
                  </button>
                ) : (
                  <button
                    className="admin-btn admin-btn-edit"
                    onClick={submitEdit}
                  >
                    <i className="fa-solid fa-save" style={{ marginRight: 5 }}></i>
                    Lưu thay đổi
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {view === 'categories' && (
      <div className="admin-table-card">
        <div className="admin-table-header">
          <div></div>
          <div className="admin-filters">
            <button onClick={openCategoryCreate} className="admin-btn admin-btn-edit">
              <i className="fa-solid fa-plus" style={{ marginRight: 5 }}></i>
              Thêm chủ đề
            </button>
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tên chủ đề</th>
                <th>Mô tả</th>
                <th>Trạng thái</th>
                <th>Hành động</th>
              </tr>
            </thead>
            <tbody>
              {categoryLoading ? (
                <tr><td colSpan={5} className="admin-loading"><i className="fa-solid fa-spinner fa-spin"></i> Đang tải...</td></tr>
              ) : categoryError ? (
                <tr><td colSpan={5} className="admin-error"><i className="fa-solid fa-circle-exclamation"></i> {categoryError}</td></tr>
              ) : categoryRows.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#747d8c' }}>Không có dữ liệu</td></tr>
              ) : (
                categoryRows.map((row) => (
                  <tr key={row.CategoryID}>
                    <td>#{row.CategoryID}</td>
                    <td style={{ fontWeight: 500 }}>{row.Name}</td>
                    <td>{row.Description || <span style={{ color: '#a4b0be' }}>—</span>}</td>
                    <td>
                      {row.Status ? (
                        <span className="admin-badge admin-badge-success">Hoạt động</span>
                      ) : (
                        <span className="admin-badge admin-badge-secondary">Không hoạt động</span>
                      )}
                    </td>
                    <td>
                      <div className="admin-action-links">
                        <button className="admin-btn" onClick={() => toggleCategoryStatus(row)}>
                          <i className="fa-solid fa-power-off"></i> Trạng thái
                        </button>
                        <button className="admin-btn admin-btn-edit" onClick={() => openCategoryEdit(row)}>
                          <i className="fa-solid fa-pen-to-square"></i> Sửa
                        </button>
                        <button className="admin-btn admin-btn-delete" onClick={() => deleteCategory(row)}>
                          <i className="fa-solid fa-trash-can"></i> Xóa
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {categoryModal && (
        <div className="admin-modal-overlay" role="dialog" aria-modal="true" onClick={() => setCategoryModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="admin-modal-header">
              <h3>{categoryModal.mode === 'create' ? 'Thêm chủ đề mới' : 'Chỉnh sửa chủ đề'}</h3>
              <button type="button" className="admin-modal-close" onClick={() => setCategoryModal(null)}>
                ×
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-info-grid" style={{ gridTemplateColumns: '1fr', gap: 16 }}>
                <label>
                  <span>Tên chủ đề <span style={{ color: '#ff4757' }}>*</span></span>
                  <input
                    type="text"
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                    placeholder="Ví dụ: Sức khỏe tim mạch"
                  />
                </label>

                <label>
                  <span>Mô tả</span>
                  <textarea
                    rows={4}
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                    placeholder="Mô tả ngắn về chủ đề..."
                  />
                </label>

                <label>
                  <span>Trạng thái</span>
                  <select
                    value={String(categoryForm.status)}
                    onChange={(e) => setCategoryForm({ ...categoryForm, status: Number(e.target.value) })}
                  >
                    <option value="1">Hoạt động</option>
                    <option value="0">Không hoạt động</option>
                  </select>
                </label>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12,
                marginTop: 24,
                paddingTop: 20,
                borderTop: '1px solid #f1f2f6'
              }}>
                <button
                  className="admin-btn"
                  style={{ background: '#a4b0be', color: 'white' }}
                  onClick={() => setCategoryModal(null)}
                >
                  Hủy
                </button>
                {categoryModal.mode === 'create' ? (
                  <button
                    className="admin-btn admin-btn-view"
                    onClick={submitCategoryCreate}
                  >
                    <i className="fa-solid fa-plus" style={{ marginRight: 5 }}></i>
                    Tạo chủ đề
                  </button>
                ) : (
                  <button
                    className="admin-btn admin-btn-edit"
                    onClick={submitCategoryEdit}
                  >
                    <i className="fa-solid fa-save" style={{ marginRight: 5 }}></i>
                    Lưu thay đổi
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
